import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  customersAPI,
  interactionsAPI,
  ordersAPI,
  notesAPI,
} from "../services/api";
import InteractionModal from "../components/InteractionModal";
import OrderModal from "../components/OrderModal";
import NoteModal from "../components/NoteModal";
import DataTable from "../components/DataTable";

const METHOD_BADGE = {
  Visited:      'badge-success',
  'Phone Call': 'badge-info',
  Message:      'badge-warning',
  'Video Call': 'badge-info',
  Meeting:      'badge-gray',
};

const CustomerDetails = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchCustomerDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await customersAPI.getById(id);
      setCustomer(response.data);
    } catch (err) {
      setError("Failed to load customer details");
      console.error("Customer details error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomerDetails();
  }, [fetchCustomerDetails]);

  const handleDeleteInteraction = async (interactionId) => {
    if (window.confirm("Are you sure you want to delete this interaction?")) {
      try {
        await interactionsAPI.delete(interactionId);
        fetchCustomerDetails();
      } catch (err) {
        alert("Failed to delete interaction");
      }
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await ordersAPI.delete(orderId);
        fetchCustomerDetails();
      } catch (err) {
        alert("Failed to delete order");
      }
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      try {
        await notesAPI.delete(noteId);
        fetchCustomerDetails();
      } catch (err) {
        alert("Failed to delete note");
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const s = String(dateString).slice(0, 10);
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-KE');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 sm:space-y-6 px-2 sm:px-0">
        <div className="h-6 sm:h-8 bg-gray-200 rounded mb-4"></div>
        <div className="card">
          <div className="card-body space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <div className="text-red-600 mb-4 text-sm sm:text-base">{error}</div>
        <button onClick={fetchCustomerDetails} className="btn btn-primary text-sm">
          Retry
        </button>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-8 sm:py-12 px-4">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">
          Customer not found
        </h3>
        <Link to="/customers" className="btn btn-primary mt-4 text-sm">
          Back to Customers
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: "overview", name: "Overview" },
    { id: "interactions", name: `Interactions (${customer.interactions?.length || 0})` },
    { id: "orders", name: `Orders (${customer.orders?.length || 0})` },
    { id: "notes", name: `Notes (${customer.notes?.length || 0})` },
  ];

  const displayNumber = customer.mobile_number || customer.contact_number || "N/A";
  const safeName = customer.customer_name?.replace(/\s+/g, '-') || 'customer';

  /* ── interaction columns ──────────────────────────────────────────── */
  const interactionColumns = [
    {
      key: '_no', label: '#',
      sortable: false, export: false,
      thClass: 'w-12',
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: 'contact_method', label: 'Method',
      exportValue: r => r.contact_method || '',
      render: val => (
        <span className={`badge text-xs ${METHOD_BADGE[val] || 'badge-secondary'}`}>
          {val || 'N/A'}
        </span>
      ),
    },
    {
      key: 'contact_date', label: 'Date',
      exportValue: r => formatDate(r.contact_date),
      render: (val, row) => (
        <div className="text-sm">
          <div>{formatDate(val)}</div>
          {row.scheduled_time && (
            <div className="text-xs text-gray-500">at {row.scheduled_time}</div>
          )}
        </div>
      ),
    },
    {
      key: 'is_scheduled', label: 'Type',
      exportValue: r => r.is_scheduled === 1 ? 'Scheduled' : 'Walk-in',
      render: val => val === 1
        ? <span className="badge badge-warning text-xs">Scheduled</span>
        : <span className="badge badge-gray text-xs">Walk-in</span>,
    },
    {
      key: 'notes', label: 'Notes',
      exportValue: r => r.notes || '',
      tdClass: 'max-w-xs',
      render: val => val
        ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
        : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'created_by_name', label: 'Logged By',
      exportValue: r => r.created_by_name || '',
      render: (val, row) => (
        <div className="text-xs">
          <div>{val || '—'}</div>
          <div className="text-gray-400">{formatDateTime(row.created_at)}</div>
        </div>
      ),
    },
    {
      key: 'actions', label: '',
      sortable: false, export: false,
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setEditingItem(row); setShowInteractionModal(true); }}
            className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteInteraction(row.id)}
            className="btn btn-ghost p-1.5 text-red-500 hover:text-red-700"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  /* ── order columns ────────────────────────────────────────────────── */
  const orderColumns = [
    {
      key: '_no', label: '#',
      sortable: false, export: false,
      thClass: 'w-12',
      render: (_, __, idx) => (
        <span className="text-gray-400 font-mono text-xs">{idx + 1}</span>
      ),
    },
    {
      key: 'order_status', label: 'Status',
      exportValue: r => r.order_status,
      render: val => (
        <span className={`badge text-xs ${val === 'Ordered' ? 'badge-success' : 'badge-danger'}`}>
          {val === 'Ordered' ? 'Completed' : 'Pending'}
        </span>
      ),
    },
    {
      key: 'order_amount', label: 'Amount (KSH)',
      exportValue: r => parseFloat(r.order_amount || 0).toFixed(2),
      render: val => (
        <span className="font-semibold text-success-700">
          {formatCurrency(val || 0)}
        </span>
      ),
    },
    {
      key: 'order_date', label: 'Order Date',
      exportValue: r => formatDate(r.order_date),
      render: val => formatDate(val),
    },
    {
      key: 'salesperson_name', label: 'Salesperson',
      exportValue: r => r.salesperson_name || '',
      render: val => val || <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'notes', label: 'Notes',
      exportValue: r => r.notes || '',
      tdClass: 'max-w-xs',
      render: val => val
        ? <span className="text-gray-700 text-xs line-clamp-2">{val}</span>
        : <span className="text-gray-400 text-xs">—</span>,
    },
    {
      key: 'created_by_name', label: 'Created By',
      exportValue: r => r.created_by_name || '',
      render: (val, row) => (
        <div className="text-xs">
          <div>{val || '—'}</div>
          <div className="text-gray-400">{formatDateTime(row.created_at)}</div>
        </div>
      ),
    },
    {
      key: 'actions', label: '',
      sortable: false, export: false,
      render: (_, row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setEditingItem(row); setShowOrderModal(true); }}
            className="btn btn-ghost p-1.5 text-gray-600 hover:text-primary-600"
            title="Edit"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteOrder(row.id)}
            className="btn btn-ghost p-1.5 text-red-500 hover:text-red-700"
            title="Delete"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <Link to="/customers" className="btn btn-ghost p-1 sm:p-2">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
              {customer.customer_name}
            </h1>
            <p className="text-xs sm:text-sm text-gray-600">{displayNumber}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
          <button
            onClick={() => { setEditingItem(null); setShowInteractionModal(true); }}
            className="btn btn-secondary text-xs sm:text-sm w-full sm:w-auto justify-center"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="hidden sm:inline">Log Interaction</span>
            <span className="sm:hidden">Interaction</span>
          </button>
          <button
            onClick={() => { setEditingItem(null); setShowOrderModal(true); }}
            className="btn btn-primary text-xs sm:text-sm w-full sm:w-auto justify-center"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <span className="hidden sm:inline">Add Order</span>
            <span className="sm:hidden">Order</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 -mx-2 sm:mx-0 px-2 sm:px-0">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4 sm:mt-6">

        {/* ── OVERVIEW ───────────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Customer Info */}
            <div className="lg:col-span-2">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Information</h3>
                </div>
                <div className="card-body">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">Name</dt>
                      <dd className="mt-1 text-xs sm:text-sm text-gray-900 break-words">{customer.customer_name}</dd>
                    </div>
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">Mobile Number</dt>
                      <dd className="mt-1 text-xs sm:text-sm text-gray-900">{displayNumber}</dd>
                    </div>
                    {customer.email && (
                      <div>
                        <dt className="text-xs sm:text-sm font-medium text-gray-500">Email</dt>
                        <dd className="mt-1 text-xs sm:text-sm text-gray-900 break-all">{customer.email}</dd>
                      </div>
                    )}
                    {customer.county && (
                      <div>
                        <dt className="text-xs sm:text-sm font-medium text-gray-500">County</dt>
                        <dd className="mt-1 text-xs sm:text-sm text-gray-900">{customer.county}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">Customer Since</dt>
                      <dd className="mt-1 text-xs sm:text-sm text-gray-900">{formatDate(customer.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500">Last Updated</dt>
                      <dd className="mt-1 text-xs sm:text-sm text-gray-900">{formatDateTime(customer.updated_at)}</dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 lg:grid-cols-1 gap-3 sm:gap-6">
              <div className="card">
                <div className="card-body text-center p-3 sm:p-4">
                  <div className="text-lg sm:text-2xl font-bold text-primary-600">{customer.interactions?.length || 0}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Interactions</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center p-3 sm:p-4">
                  <div className="text-lg sm:text-2xl font-bold text-success-600">{customer.orders?.length || 0}</div>
                  <div className="text-xs sm:text-sm text-gray-600">Orders</div>
                </div>
              </div>
              <div className="card">
                <div className="card-body text-center p-3 sm:p-4">
                  <div className="text-sm sm:text-2xl font-bold text-warning-600 break-words">
                    {formatCurrency(
                      customer.orders
                        ?.filter((o) => o.order_status === "Ordered")
                        .reduce((total, order) => total + parseFloat(order.order_amount || 0), 0) || 0
                    )}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600">Revenue</div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="lg:col-span-3">
              <div className="card">
                <div className="card-header">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Recent Activity</h3>
                </div>
                <div className="card-body p-0">
                  <div className="divide-y divide-gray-200 max-h-64 sm:max-h-96 overflow-y-auto">
                    {[
                      ...(customer.interactions || []).map((i) => ({ ...i, type: "interaction" })),
                      ...(customer.orders || []).map((o) => ({ ...o, type: "order" })),
                    ]
                      .sort((a, b) =>
                        new Date(b.contact_date || b.order_date) - new Date(a.contact_date || a.order_date)
                      )
                      .slice(0, 10)
                      .map((item) => (
                        <div key={`${item.type}-${item.id}`} className="p-3 sm:p-4 hover:bg-gray-50">
                          <div className="flex items-start space-x-2 sm:space-x-3">
                            <div className="flex-shrink-0">
                              <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                                item.type === "interaction" ? "bg-blue-100" : "bg-green-100"
                              }`}>
                                {item.type === "interaction" ? (
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                  </svg>
                                ) : (
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                  </svg>
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                                <p className="text-xs sm:text-sm font-medium text-gray-900">
                                  {item.type === "interaction"
                                    ? `${item.contact_method} Contact`
                                    : `Order ${item.order_status}`}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {formatDate(item.contact_date || item.order_date)}
                                </p>
                              </div>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                                {item.type === "interaction"
                                  ? item.notes || "No notes"
                                  : `${formatCurrency(item.order_amount || 0)} - ${item.notes || "No notes"}`}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    {[...(customer.interactions || []), ...(customer.orders || [])].length === 0 && (
                      <div className="p-4 text-center text-gray-500 text-xs sm:text-sm">
                        No activity recorded yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── INTERACTIONS ───────────────────────────────────────────── */}
        {activeTab === "interactions" && (
          <div className="card">
            <div className="card-header flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">All Interactions</h3>
              <button
                onClick={() => { setEditingItem(null); setShowInteractionModal(true); }}
                className="btn btn-primary text-xs sm:text-sm"
              >
                Add Interaction
              </button>
            </div>
            <div className="card-body">
              <DataTable
                data={customer.interactions || []}
                columns={interactionColumns}
                searchFields={['contact_method', 'notes', 'created_by_name']}
                filename={`${safeName}-interactions`}
                title={`${customer.customer_name} – Interactions`}
                loading={false}
                rowKey={row => row.id}
              />
            </div>
          </div>
        )}

        {/* ── ORDERS ─────────────────────────────────────────────────── */}
        {activeTab === "orders" && (
          <div className="card">
            <div className="card-header flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">All Orders</h3>
              <button
                onClick={() => { setEditingItem(null); setShowOrderModal(true); }}
                className="btn btn-primary text-xs sm:text-sm"
              >
                Add Order
              </button>
            </div>
            <div className="card-body">
              <DataTable
                data={customer.orders || []}
                columns={orderColumns}
                searchFields={['order_status', 'notes', 'created_by_name', 'salesperson_name']}
                filename={`${safeName}-orders`}
                title={`${customer.customer_name} – Orders`}
                loading={false}
                rowKey={row => row.id}
              />
            </div>
          </div>
        )}

        {/* ── NOTES ──────────────────────────────────────────────────── */}
        {activeTab === "notes" && (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Customer Notes</h3>
              <button
                onClick={() => { setEditingItem(null); setShowNoteModal(true); }}
                className="btn btn-primary text-xs sm:text-sm w-full sm:w-auto"
              >
                Add Note
              </button>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {(customer.notes || []).map((note) => (
                <div key={note.id} className="card">
                  <div className="card-body p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-700 mb-2 break-words">{note.note}</p>
                        <p className="text-xs text-gray-500">
                          Added by {note.created_by_name} on {formatDateTime(note.created_at)}
                        </p>
                      </div>
                      <div className="flex sm:flex-col gap-2 justify-end sm:justify-start">
                        <button
                          onClick={() => { setEditingItem(note); setShowNoteModal(true); }}
                          className="btn btn-ghost p-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.id)}
                          className="btn btn-ghost p-2 text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {(!customer.notes || customer.notes.length === 0) && (
                <div className="text-center py-6 sm:py-8">
                  <svg className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No notes yet</h3>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500">Add notes about this customer</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showInteractionModal && (
        <InteractionModal
          interaction={editingItem}
          customerId={customer.id}
          onClose={() => {
            setShowInteractionModal(false);
            setEditingItem(null);
            fetchCustomerDetails();
          }}
        />
      )}

      {showOrderModal && (
        <OrderModal
          order={editingItem}
          customerId={customer.id}
          onClose={() => {
            setShowOrderModal(false);
            setEditingItem(null);
            fetchCustomerDetails();
          }}
        />
      )}

      {showNoteModal && (
        <NoteModal
          note={editingItem}
          customerId={customer.id}
          onClose={() => {
            setShowNoteModal(false);
            setEditingItem(null);
            fetchCustomerDetails();
          }}
        />
      )}
    </div>
  );
};

export default CustomerDetails;
